/**
 * FO-113 (batch-22) — verklaring zakelijk/privé-gebruik (auto) + bijtelling-koppeling.
 *
 * Gedeeld request-schema (API `.safeParse` + client-side inline-veldfouten via
 * `issuesToFieldErrors`) + read-DTO's en label-maps voor portal én backoffice.
 *
 * De keuze `type` stuurt `Employee.bijtelling` (één bron, FO-038) server-side:
 * UITSLUITEND_ZAKELIJK => bijtelling Nee, OOK_PRIVE => bijtelling Ja. De
 * verklaring kopieert niets; ze schrijft op de actieve EmployeeContractMutation
 * (de bron die `recomputeCurrentEmployeeState` naar `Employee.bijtelling` spiegelt).
 */
import { z } from 'zod'

// ─── Enums ────────────────────────────────────────────────────────────────────

export const VOERTUIG_GEBRUIK_TYPE = ['UITSLUITEND_ZAKELIJK', 'OOK_PRIVE'] as const
export type VoertuigGebruikType = (typeof VOERTUIG_GEBRUIK_TYPE)[number]

export const VOERTUIG_GEBRUIK_TYPE_LABELS: Record<VoertuigGebruikType, string> = {
  UITSLUITEND_ZAKELIJK: 'Uitsluitend zakelijk (geen privé)',
  OOK_PRIVE:            'Ook privégebruik',
}

/** Korte badge-tekst (compacter dan het volledige keuze-label). */
export const VOERTUIG_GEBRUIK_TYPE_BADGE: Record<VoertuigGebruikType, string> = {
  UITSLUITEND_ZAKELIJK: 'Zakelijk',
  OOK_PRIVE:            'Ook privé',
}

/** Gevolg voor de bijtelling, in gewone taal (portal-uitleg). */
export const VOERTUIG_GEBRUIK_TYPE_BIJTELLING_UITLEG: Record<VoertuigGebruikType, string> = {
  UITSLUITEND_ZAKELIJK:
    'Geen bijtelling: je gebruikt de auto uitsluitend zakelijk en houdt eventueel een rittenregistratie bij.',
  OOK_PRIVE:
    'Wel bijtelling: omdat je de auto ook privé gebruikt, wordt de fiscale bijtelling op je loon toegepast.',
}

/**
 * Status van de verklaring per toewijzing (badge in het bestuurders-overzicht +
 * portal-actie). Server-derived uit de laatste niet-verwijderde verklaring:
 *   - GEEN                 : VAST-toewijzing zonder (geldige) verklaring -> open.
 *   - ZAKELIJK / OOK_PRIVE : geldige verklaring van dat type.
 *   - HERBEVESTIGING_NODIG : laatste verklaring is verlopen (`geldigTot < now`).
 * `null` op de rij = niet van toepassing (TIJDELIJK zonder verklaring).
 */
export const VOERTUIG_VERKLARING_STATUS = [
  'GEEN',
  'ZAKELIJK',
  'OOK_PRIVE',
  'HERBEVESTIGING_NODIG',
] as const
export type VoertuigVerklaringStatus = (typeof VOERTUIG_VERKLARING_STATUS)[number]

export const VOERTUIG_VERKLARING_STATUS_LABELS: Record<VoertuigVerklaringStatus, string> = {
  GEEN:                 'Open',
  ZAKELIJK:             'Zakelijk',
  OOK_PRIVE:            'Ook privé',
  HERBEVESTIGING_NODIG: 'Herbevestiging nodig',
}

export const VOERTUIG_VERKLARING_STATUS_BADGE: Record<
  VoertuigVerklaringStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  GEEN:                 'warning',
  ZAKELIJK:             'secondary',
  OOK_PRIVE:            'info',
  HERBEVESTIGING_NODIG: 'warning',
}

// ─── Request-schema (gedeeld API + client) ──────────────────────────────────────

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v == null || v.trim() === '' ? null : v))

/**
 * Vastleggen/herbevestigen van een verklaring. `akkoord` (het "Ik verklaar dit
 * naar waarheid"-vinkje) is verplicht true op beide paden (portal + HR-namens).
 * De handtekening is optioneel (akkoord-zonder-handtekening voor HR-namens, zoals
 * onboarding); de portal-flow zet 'm standaard wél.
 */
export const voertuigVerklaringSchema = z.object({
  type: z.enum(VOERTUIG_GEBRUIK_TYPE),
  akkoord: z
    .boolean()
    .refine((v) => v === true, { message: 'Bevestig de verklaring naar waarheid' }),
  // base64-PNG (data-URL of pure base64). Ruime bovengrens tegen misbruik; een
  // canvas-handtekening is in de praktijk < 100 kB.
  handtekening: z
    .string()
    .trim()
    .max(3_000_000)
    .optional()
    .nullable()
    .transform((v) => (v == null || v.trim() === '' ? null : v)),
  notitie: optionalText(1000),
  redenZakelijk: optionalText(1000),
})
export type VoertuigVerklaringInput = z.infer<typeof voertuigVerklaringSchema>

// ─── Read-DTO's ─────────────────────────────────────────────────────────────────

/** Eén verklaring-record (zonder de rauwe base64-handtekening). */
export interface VoertuigVerklaringDTO {
  id:                  string
  toewijzingId:        string
  employeeId:          string
  type:                VoertuigGebruikType
  /** Moment van akkoord (ISO, UTC). Toon in `Europe/Amsterdam`. */
  verklaardOp:         string
  /** "MEDEWERKER" of de userId van de HR-medewerker die het namens hem vastlegde. */
  ondertekendDoor:     string
  /** Resolved weergavenaam: "Medewerker" of de naam van de HR-ondertekenaar. */
  ondertekendDoorNaam: string | null
  /** Of er een canvas-handtekening is vastgelegd (de base64 zelf wordt niet gelekt). */
  heeftHandtekening:   boolean
  notitie:             string | null
  redenZakelijk:       string | null
  /** Eind geldigheid (ISO, UTC) of null. */
  geldigTot:           string | null
  bijtellingGevolg:    boolean
  /** Server-derived: niet verwijderd, niet ingetrokken, al ingegaan én (geen geldigTot óf geldigTot in de toekomst). */
  isGeldig:            boolean
  createdAt:           string
  // ── batch-67 campagnes (FO-501) ─────────────────────────────────────────
  /** Ingangsdatum (ISO); bij oudere rijen gelijk aan `verklaardOp`. */
  geldigVanaf:         string
  /** Kalenderjaar (Europe/Amsterdam) waarvoor de verklaring geldt. */
  jaar:                number
  /** Ingangsdatum in de toekomst (campagne voor volgend jaar). */
  isGepland:           boolean
  /** De actieve verklaring van dit jaar (één per voertuig en per medewerker). */
  isActief:            boolean
  ingetrokkenOp:       string | null
  /** WISSEL | CORRECTIE | VERVANGEN */
  ingetrokkenReden:    string | null
}

/**
 * Context van de verklaringen op één toewijzing (`GET /voertuigen/toewijzingen/
 * :id/verklaring`): de huidige status, de actieve verklaring en de volledige
 * historie (read-only, nieuw -> oud).
 */
export interface VoertuigVerklaringContextDTO {
  toewijzingId: string
  employeeId:   string
  /** Of deze toewijzing standaard een verklaring vraagt (VAST = ja). */
  vereistVerklaring: boolean
  status:       VoertuigVerklaringStatus
  actief:       VoertuigVerklaringDTO | null
  /** batch-67: de al vastgelegde verklaring voor volgend jaar (campagne), of null. */
  gepland:      VoertuigVerklaringDTO | null
  historie:     VoertuigVerklaringDTO[]
}
